<template>
  <div class="signature-pad-container">
    <div class="canvas-wrapper" ref="canvasWrapper">
      <div class="canvas-inner" :style="canvasStyle">
        <VueSignaturePad
          :width="canvasWidth + 'px'"
          :height="canvasHeight + 'px'"
          :options="signaturePadOptions"
          ref="signaturePad"
        />
      </div>
    </div>
    <div class="button-group">
      <button @click="save" class="btn btn-dc btn-block btn-save">
        <i class="fa fa-check"></i> Submit Signature
      </button>
      <button @click="undo" class="btn btn-red btn-block btn-clear">
        <i class="fa fa-redo"></i> Clear Signature
      </button>
    </div>
  </div>
</template>
<script>
export default {
  name: 'DsSignaturePad',
  data() {
    return {
      canvasWidth: 600,
      canvasHeight: 250,
      isMobile: false,
      isLandscape: false,
      currentSignatureData: null,
      resizeDebounceTimer: null,
      signaturePadOptions: {
        dotSize: 2,
        minWidth: 1.5,
        maxWidth: 3.5,
        backgroundColor: 'rgba(255, 255, 255, 1)',
        penColor: 'rgba(0, 0, 0, 1)',
        velocityFilterWeight: 0.7
      }
    };
  },
  computed: {
    canvasStyle() {
      return {
        border: '1px solid #cbd5e0',
        borderRadius: '8px',
        backgroundColor: '#ffffff'
      };
    }
  },
  mounted() {
    this.detectDevice();
    this.calculateCanvasSize();

    // Add event listeners for responsive sizing
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleOrientationChange);
  },
  beforeDestroy() {
    // Clean up event listeners
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleOrientationChange);

    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }
  },
  methods: {
    detectDevice() {
      // Detect if mobile device
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      this.isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

      // Detect orientation
      this.detectOrientation();
    },

    detectOrientation() {
      if (window.matchMedia) {
        this.isLandscape = window.matchMedia('(orientation: landscape)').matches;
      } else {
        this.isLandscape = window.innerWidth > window.innerHeight;
      }
    },

    calculateCanvasSize() {
      this.$nextTick(() => {
        if (!this.$refs.canvasWrapper) return;

        const containerWidth = this.$refs.canvasWrapper.clientWidth;

        if (this.isMobile) {
          // Mobile: Use container-based dimensions
          this.canvasWidth = containerWidth - 20;

          if (this.isLandscape) {
            // Landscape: shorter height
            this.canvasHeight = Math.min(window.innerHeight - 200, 200);
          } else {
            // Portrait: taller height
            this.canvasHeight = 250;
          }
        } else {
          // Desktop: Use responsive width with fixed aspect ratio
          this.canvasWidth = Math.min(containerWidth - 20, 600);
          this.canvasHeight = 250;
        }

        // Ensure minimum sizes
        this.canvasWidth = Math.max(this.canvasWidth, 280);
        this.canvasHeight = Math.max(this.canvasHeight, 150);
      });
    },

    handleResize() {
      // Debounce resize handler
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }

      this.resizeDebounceTimer = setTimeout(() => {
        this.detectOrientation();

        // Save current signature data before resize
        if (this.$refs.signaturePad) {
          const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
          this.currentSignatureData = isEmpty ? null : data;
        }

        // Recalculate canvas size
        this.calculateCanvasSize();

        // Restore signature data after resize
        this.$nextTick(() => {
          if (this.currentSignatureData && this.$refs.signaturePad) {
            this.$refs.signaturePad.fromDataURL(this.currentSignatureData);
          }
        });
      }, 150);
    },

    handleOrientationChange() {
      // Wait for orientation change to complete
      setTimeout(() => {
        this.detectOrientation();
        this.handleResize();
      }, 100);
    },

    undo() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
        this.currentSignatureData = null;
      }
    },

    save() {
      if (!this.$refs.signaturePad) return;

      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();

      if (isEmpty) {
        alert('Please draw your signature first.');
        return;
      }

      this.currentSignatureData = data;
      this.$emit('signature-saved', data);
    }
  }
};
</script>

<style scoped>
.signature-pad-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 16px;
}

.canvas-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.canvas-inner {
  display: inline-block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.canvas-inner >>> canvas {
  display: block;
  cursor: crosshair;
  touch-action: none;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
}

.button-group .btn {
  min-height: 44px;
  font-weight: 600;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-save {
  background-color: #4299e1;
  border-color: #4299e1;
  color: #ffffff;
}

.btn-save:hover {
  background-color: #3182ce;
  border-color: #3182ce;
}

.btn-clear {
  background-color: #f7fafc;
  border-color: #cbd5e0;
  color: #4a5568;
}

.btn-clear:hover {
  background-color: #edf2f7;
}

/* Mobile Optimizations */
@media (max-width: 768px) {
  .signature-pad-container {
    padding: 12px 16px;
  }

  .button-group {
    gap: 10px;
  }

  .button-group .btn {
    font-size: 14px;
  }
}
</style>
